import './App.scss';
import React, { useEffect, useState } from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import axios from 'axios';
import { useCookies } from 'react-cookie';

import Footer from './components/Footer/Footer';
import Header from './components/Header/Header';

import MainSteps from './components/MainSteps/MainSteps';
import Login from './components/Login/Login';
import UserAccount from './components/UserAccount/UserAccount';
import DownloadCertificate from './components/DownloadCertificate/DownloadCertificate';
import UpdateUserDetails from './components/UpdateUserDetails/UpdateUserDetails';
import UpdatePaymentDetails from './components/UpdatePaymentDetails/UpdatePaymentDetails';
import UpdateUserPassword from './components/UpdateUserPassword/UpdateUserPassword';
import UpdateCreditCard from './components/UpdateCreditCard/UpdateCreditCard';
import CancelSubscription from './components/CancelSubscription/CancelSubscription';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import ForgotPasswordSet from './components/ForgotPasswordSet/ForgotPasswordSet';
import SetPassword from './components/SetPassword/SetPassword';
import AffiliateLanding from './components/AffiliateLanding/AffiliateLanding';
import RecordingToken from './components/RecordingToken/RecordingToken';
import Demo from './components/Demo/Demo';

import './App.scss';
import { isPhone } from './services/deviceService';
import AuthRoute from './AuthRoute';
import { APP_VERSION, SERVER_URL } from './config';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { saveAffiliate } from './features/affiliate/affiliateSlice';
import { selectSignIn } from './features/signIn/signInSlice';
import { Authorization } from './services/authorization';
import TenantContext from './contexts/tenantContext';
import { getAffiliateById, getCurrentUser } from './services/axios/requests';
import { User } from './models/user';
import CurrentUserContext from './contexts/currentUserContext';
import { Tenant } from './models/tenant';
import { Utils } from './services/utils';
import { trackPageView } from './services/analyticsService';
import ConsentBanner from './components/ConsentBanner/ConsentBanner';


function App()
{
  const location = useLocation();
  const [tenant, setTenant] = React.useState({} as Tenant)
  const [currentUser, setCurrentUser] = React.useState<User | null>(null)
  const [cookies, setCookie] = useCookies(['tenant'], {
    doNotParse: true
  });

  const dispatch = useAppDispatch();
  const isSignedIn = useAppSelector(selectSignIn);

  useEffect(() =>
  {
    console.info(APP_VERSION);

    window.addEventListener("message", (event: any) =>
    {

      if (!event.origin.includes('makeawillonline.co.uk'))
      {
        return;
      }

      sessionStorage.setItem("iframe-name", event.data.name);
      sessionStorage.setItem("iframe-dateOfBirth", event.data.dateOfBirth ?? "");
      sessionStorage.setItem("iframe-email", event.data.email ?? "");
      sessionStorage.setItem("iframe-address1", event.data.address1 ?? "");
      sessionStorage.setItem("iframe-address2", event.data.address2 ?? "");
      sessionStorage.setItem("iframe-town", event.data.town ?? "");
      sessionStorage.setItem("iframe-postcode", event.data.postcode ?? "");
      sessionStorage.setItem("iframe-county", event.data.county ?? "");
    });
  }, [])


  useEffect(() =>
  {
    trackPageView(location.pathname);
  }, [location]);

  useEffect(() =>
  {
    Utils.scrollToTop();
  }, [location.pathname, location.search]);

  const getQuery = () =>
  {
    return new URLSearchParams(location.search);
  }

  const isHorizontal = () =>
  {
    // Apple does not seem to have the window.screen api so we have to use deprecated window.orientation instead.
    if (window.orientation && typeof window.orientation === "number" && Math.abs(window.orientation) === 90)
    {
      return true;
    }
    if (window.screen.orientation && window.screen.orientation.type.includes('/^landscape-.+$/') === true)
    {
      return true;
    }
    return false;
  };
  const [horizontal, setHorizontal] = React.useState(isHorizontal());

  const getTenantUrl = () =>
  {

    let query = getQuery();
    var tenantUrl = query.get("tenant");

    if (tenantUrl && tenantUrl !== "")
    {
      setCookie("tenant", tenantUrl);
    } else if (cookies.tenant)
    {
      tenantUrl = cookies.tenant;
    } else
    {
      tenantUrl = "default";
      setCookie("tenant", tenantUrl);
    }

    return tenantUrl;
  }

  const getTenant = async () =>
  {
    try
    {
      var tenantUrl = getTenantUrl();

      let response = await axios.get(SERVER_URL + `/tenant?tenantUrl=${tenantUrl}`);

      // The endpoint answers 200 with no tenantId when the tenant does not exist in this environment.
      if (response?.status === 200 && !response.data?.tenantId && tenantUrl !== "default")
      {
        console.log(`tenant "${tenantUrl}" not found, using the default tenant`);
        setCookie("tenant", "default");
        response = await axios.get(SERVER_URL + `/tenant?tenantUrl=default`);
      }

      if (response && response.status === 200)
      {
        setTenant(response.data)
      } else
      {
        console.log('can`t get tenant');
      }
    } catch
    {
      console.log('can`t get tenant');
    }
  }

  useEffect(() =>
  {
    getTenant()
    window.addEventListener("orientationchange", () =>
    {
      setHorizontal(isHorizontal());
    });
    // eslint-disable-next-line
  }, [location]);

  const [signedIn, setSignedIn] = useState(false);

  if (Authorization.GetUsername())
  {
    if (!signedIn)
      setSignedIn(true);
  }

  // The redux flag covers sign-up/sign-in within this session, which doesn't re-render App.
  useEffect(() =>
  {
    if (signedIn || isSignedIn)
    {
      retrieveCurrentUser();
    }
    // eslint-disable-next-line
  }, [signedIn, isSignedIn]);

  useEffect(() =>
  {
    if (currentUser?.affiliateId)
    {
      retrieveAffiliate();
    }
    // eslint-disable-next-line
  }, [currentUser]);

  const retrieveAffiliate = async () =>
  {
    try
    {
      if (currentUser?.affiliateId)
      {
        const affiliate = await getAffiliateById(currentUser.affiliateId);

        dispatch(saveAffiliate(affiliate.data));
      }

    } catch (e)
    {
      console.log('affiliate error');
      console.error(e);
    }
  }

  const retrieveCurrentUser = async () =>
  {
    try
    {
      const response = await getCurrentUser();

      if (response?.data){
        console.log('retrieved current user', JSON.stringify(response.data));
        setCurrentUser({...response.data});
      }

    } catch (e)
    {
      console.log('current user error');
      console.error(e);
    }
  }

  return (
    <div className="App">
      {/* The cookie banner. Nothing of Google's loads until it is accepted. */}
      <ConsentBanner />
      {tenant?.tenantId && <TenantContext.Provider value={tenant}>
        <CurrentUserContext.Provider value={{
          User: currentUser,
          SetUser: setCurrentUser
      }}>
          <div style={{ minHeight: '100vh' }}>
            <Switch>
              <Route exact path="/">
                <Header signedIn={signedIn} />
                <MainSteps />
              </Route>
              <Route exact path="/demo">
                <Demo />
              </Route>
              <Route path="/login">
                <Header signedIn={signedIn} />
                <Login />
              </Route>
              <Route path="/affiliate/:url">
                <Header signedIn={signedIn} />
                <AffiliateLanding />
              </Route>
              <Route path="/affiliateFrame/:affiliateId">
                <Header signedIn={signedIn} />
                <AffiliateLanding />
              </Route>
              <Route path="/confirmPassword">
                <Header signedIn={signedIn} />
                <ForgotPasswordSet />
              </Route>
              <AuthRoute path="/userAccount">
                <Header signedIn={signedIn} />
                <UserAccount />
              </AuthRoute>
              <AuthRoute path="/downloadCertificate">
                <Header signedIn={signedIn} />
                <DownloadCertificate />
              </AuthRoute>
              <AuthRoute path="/updateUserDetails">
                <Header signedIn={signedIn} />
                <UpdateUserDetails />
              </AuthRoute>
              <AuthRoute path="/updatePaymentDetails">
                <Header signedIn={signedIn} />
                <UpdatePaymentDetails />
              </AuthRoute>
              <Route path="/resetPassword">
                <Header signedIn={signedIn} />
                <UpdateUserPassword />
              </Route>
              <AuthRoute path="/UpdateCreditCard">
                <Header signedIn={signedIn} />
                <UpdateCreditCard />
              </AuthRoute>
              <AuthRoute path="/cancelSubscription">
                <Header signedIn={signedIn} />
                <CancelSubscription />
              </AuthRoute>
              <Route path="/forgotPassword">
                <Header signedIn={signedIn} />
                <ForgotPassword />
              </Route>
              <Route path="/setPassword">
                <Header signedIn={signedIn} />
                <SetPassword />
              </Route>
              <Route path="/recording-token/:token">
                <Header signedIn={signedIn} />
                <RecordingToken />
              </Route>
            </Switch>
          </div>
          {isPhone() && horizontal && <Dialog className="RotatePopup" open={true}>
            <DialogTitle className="ft-25">
              Please rotate device to continue
            </DialogTitle>
          </Dialog>}
          {!location.pathname.includes("/demo") &&
            <Footer />
          }
        </CurrentUserContext.Provider>
      </TenantContext.Provider>
      }
    </div>
  );
}

export default App;
